import { APIGet, APIPost, APIPut, APIDelete, StatusCode, APIUrl, AdminType } from "api/protocols";
import { CancelToken } from "axios";
import { TBookingRestriction } from "pages/secure/facility/settings/teesheets/customerTypeConfig/bookingRestrictions/BookingRestrictions";
import { IBooking, IBookingParticipant } from "redux/reducers/models/teetime";

// GET Tee Time Booking
// Retrieve an array of tee time bookings
interface IGetBooking {
  id?: number;
  tee_time_id?: number;
  customer_id?: number;
  status?: boolean;
  extended?: boolean;
  extended_tee_time?: boolean;
}

export interface IValidateDuplicateBookings {
  error_count: number;
  errors: Array<{date: string; error: string; tee_time_id: number}>
  total_attempted: number;
  valid_count: number;
  valid_dates: Array<{date: string; error: string; tee_time_id: number}>;
}

export async function GetBooking(params: IGetBooking, useGlobalLoader: boolean, token?: CancelToken) {
  return await APIGet<IBooking[]>(
    APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking"),
    params,
    useGlobalLoader,
    token,
  );
}

export async function GetTotalBookings(params: { customer_id: number }, useGlobalLoader: boolean, token?: CancelToken) {
  return await APIGet(
    APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking/participant/count"),
    params,
    useGlobalLoader,
    token,
  );
}

// POST Booking
// Create a new booking for a tee time
export interface IPostBooking {
  tee_time_id?: number;
  send_confirmation?: boolean;
  quantity?: number;
  holes?: number;
  power_cart_quantity?: number;
  customer_id?: number;
  customer_payment_method_id?: number;

  tee_time_slots?: {
    id: number;
    customer_id: number;
    guest: boolean;
    first_name?: string;
    last_name?: string;
  }[];

  booking_group_id?: string;
  source?: string;
}

export async function PostBooking(params: IPostBooking, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking"), params, useGlobalLoader);
}

// PUT Booking
// Update an existing tee time booking
export interface IPutBooking {
  id: number;
  quantity?: number;
  order_id?: number;
  customer_id?: number;
  holes?: number;
  power_cart_quantity?: number;
  customer_payment_method_id?: number;
  tee_time_slots?: { id: number; customer_id: number; guest: boolean; first_name?: string; last_name?: string }[];
  extended?: boolean;
  source?: string;
}

interface IPutBookingRes {
  status: StatusCode;
  data: {
    message?: string;
    data: {
      order_id: number;
    };
  };
  message?: string;
}

interface IPutMoveBooking {
  booking_id: number;
  tee_time_id: number;
}

export async function PutBooking(params: IPutBooking, useGlobalLoader: boolean): Promise<IPutBookingRes> {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking"), params, useGlobalLoader);
}

export interface IPutBookingWaiversPrinted {
  id: number;
  waivers_printed: number;
}

export async function PutBookingWaiversPrinted(params: IPutBookingWaiversPrinted, useGlobalLoader: boolean) {
  return await APIPut<IBooking>(
    APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking/waivers-printed"),
    params,
    useGlobalLoader,
  );
}

export async function PutMoveBooking(params: IPutMoveBooking, useGlobalLoader: boolean, cancelToken?: CancelToken) {
  return await APIPut(
    APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking/move"),
    params,
    useGlobalLoader,
    cancelToken,
  );
}

export async function PutBookingGroup(params: { booking_group_id: string; booking_group_name: string }, useGlobalLoader: boolean, cancelToken?: CancelToken) {
  return await APIPut(
    APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking/group"),
    params,
    useGlobalLoader,
    cancelToken,
  );
}

export async function PutCancelBookingGroup(params: { booking_group_id: string; send_cancellation_email: boolean }, useGlobalLoader: boolean, cancelToken?: CancelToken) {
  return await APIPut(
    APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking/group/cancel"),
    params,
    useGlobalLoader,
    cancelToken,
  );
}

// Cancel Booking
// Cancel an existing booking for a tee time
export interface IPutCancelBooking {
  tee_time_booking_id: number;
  send_cancellation_email?: boolean;
}

export interface IPutCancelBookingRes {
  status: StatusCode;
  data: any;
  message?: string;
}

// export async function PutCancelBooking(params: any, useGlobalLoader: boolean) {
//   const string = "/2021-01/facility-admin/tee-sheet/tee-time/booking/cancel?tee_time_booking_id=718";
//   return await APIPut(baseBookingUrl + "/cancel", params, useGlobalLoader);
// }

export async function PutCancelBooking(
  params: IPutCancelBooking,
  useGlobalLoader: boolean,
): Promise<IPutCancelBookingRes> {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking/cancel"), params, useGlobalLoader);
}

// Cancel participant in a booking
export interface IPutCancelParticipant {
  id: number;
}

export interface IPutCancelParticipantRes {
  status: StatusCode;
  data: any;
  message?: string;
}
export async function PutCancelParticipant(
  params: IPutCancelParticipant,
  useGlobalLoader: boolean,
): Promise<IPutCancelParticipantRes> {
  return await APIPut(
    APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking/participant/cancel"),
    params,
    useGlobalLoader,
  );
}

export interface IPutCheckParticipant {
  booking_participant_ids: number[];
}

export interface IPutCheckParticipantRes {
  status: StatusCode;
  data: any;
}

export async function PutCheckParticipant(
  params: IPutCheckParticipant,
  useGlobalLoader: boolean,
): Promise<IPutCheckParticipantRes> {
  return await APIPut(
    APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking/participant/check"),
    params,
    useGlobalLoader,
  );
}

export async function PutCheckAllParticipant(
  params: IPutCheckParticipant,
  useGlobalLoader: boolean,
): Promise<IPutCheckParticipantRes> {
  return await APIPut(
    APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking/participant/check-teesheet"),
    params,
    useGlobalLoader,
  );
}

// Add Green Fee / Power Cart to booking participant

interface IPutBookingParticipant {
  id: number;
  customer_id?: number;
  no_show?: boolean;
  no_show_weather?: boolean;
  no_show_paid?: boolean;
  green_fee_variant_id?: number;
  green_fee_paid?: boolean;
  power_cart_variant_id?: number;
  power_cart_paid?: boolean;
  order_id?: number;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  check_in_status?: string;
  power_cart_number?: number;
  power_cart_id?: number;
  seat_number?: number;
  tee_off_time?: boolean; //Backend sets the current timestamp in UTC
  turn_time?: boolean; //Backend sets the current timestamp in UTC
  finish_time?: boolean; //Backend sets the current timestamp in UTC
  cart_waiver_signature?: string;
  pending_ticket_stub_ids?: Array<number>;
}

export interface IPutBookingParticipantRes {
  status: StatusCode;
  data: IBookingParticipant;
  message: string;
}

interface IPutBookingConfirmation {
  booking_group_id: number;
}

interface IPutBookingParticipantNoShow {
  id: number;
  no_show_type: string;
  variant_id?: number;
  sendEmail?: boolean;
}

export async function PutBookingParticipant(
  params: IPutBookingParticipant,
  useGlobalLoader: boolean,
): Promise<IPutBookingParticipantRes> {
  return await APIPut(
    APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking/participant"),
    params,
    useGlobalLoader,
  );
}

export async function PutBookingParticipantNoShow(params: IPutBookingParticipantNoShow, useGlobalLoader: boolean) {
  return await APIPut<IBookingParticipant>(
    APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking/participant/noshow"),
    params,
    useGlobalLoader,
  );
}

export async function PutBookingParticipantRemoveNoShow(params: { id: number }, useGlobalLoader: boolean) {
  return await APIPut<IBookingParticipant>(
    APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking/participant/noshow/remove"),
    params,
    useGlobalLoader,
  );
}

export async function PostNoShowChargeAudit(params: any, useGlobalLoader: boolean) {
  return await APIPost(
    APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking/participant/noshowcharge/audit"),
    params,
    useGlobalLoader,
  );
}

export async function PostNoShowEmail(params: any, useGlobalLoader: boolean = true) {
  return await APIPost(
    APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking/participant/noshowcharge/email"),
    params,
    useGlobalLoader,
  );
}

export type TFutureGreenFeePreviewParticipant = {
  participant_id: number;
  booking_id: number;
  confirmation_number: string;
  tee_time_date: string;
  tee_time_start_time: string;
  course_title: string | null;
  current_green_fee_variant_id: number | null;
  current_green_fee_variant_title: string | null;
  current_green_fee_price: number | null;
  proposed_green_fee_variant_id: number | null;
  proposed_green_fee_variant_title: string | null;
  proposed_green_fee_product_title: string | null;
  proposed_green_fee_price: number | null;
  green_fee_paid: boolean;
  eligible: boolean;
  skip_reason: string | null;
};

export type TFutureGreenFeePreview = {
  participants: TFutureGreenFeePreviewParticipant[];
};

export async function GetFutureGreenFeePreview(
  params: { customer_id: number; customer_type_id: number },
  useGlobalLoader: boolean,
) {
  return await APIGet<TFutureGreenFeePreview>(
    APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking/participant/future-green-fee-preview"),
    params,
    useGlobalLoader,
  );
}

export async function PutFutureGreenFees(
  params: { customer_id: number; customer_type_id: number; participant_ids: number[] },
  useGlobalLoader: boolean,
) {
  return await APIPut(
    APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking/participant/future-green-fees"),
    params,
    useGlobalLoader,
  );
}

export type TFuturePowerCartPreviewParticipant = {
  participant_id: number;
  booking_id: number;
  confirmation_number: string;
  tee_time_date: string;
  tee_time_start_time: string;
  course_title: string | null;
  current_power_cart_variant_id: number | null;
  current_power_cart_variant_title: string | null;
  current_power_cart_price: number | null;
  proposed_power_cart_variant_id: number | null;
  proposed_power_cart_variant_title: string | null;
  proposed_power_cart_product_title: string | null;
  proposed_power_cart_price: number | null;
  power_cart_paid: boolean;
  eligible: boolean;
  skip_reason: string | null;
};

export type TFuturePowerCartPreview = {
  participants: TFuturePowerCartPreviewParticipant[];
};

export async function GetFuturePowerCartPreview(
  params: { customer_id: number; cart_customer_type_id: number },
  useGlobalLoader: boolean,
) {
  return await APIGet<TFuturePowerCartPreview>(
    APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking/participant/future-power-cart-preview"),
    params,
    useGlobalLoader,
  );
}

export async function PutFuturePowerCarts(
  params: { customer_id: number; cart_customer_type_id: number; participant_ids: number[] },
  useGlobalLoader: boolean,
) {
  return await APIPut(
    APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking/participant/future-power-carts"),
    params,
    useGlobalLoader,
  );
}

// Get/Add/Remove/Update Note to Booking
export async function GetBookingNotes(params: { tee_time_booking_id?: number; tee_time_booking_participant_id?: number }, useGlobalLoader: boolean, cancelToken?: CancelToken) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking/note"), params, useGlobalLoader, cancelToken);
}

export async function PostBookingNote(
  params: { tee_time_booking_id: number; tee_time_booking_participant_id?: number; content: string },
  useGlobalLoader: boolean,
) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking/note"), params, useGlobalLoader);
}

export async function PutBookingNote(params: { booking_note_id: number; content: string }, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking/note"), params, useGlobalLoader);
}

export async function DeleteBookingNote(params: { booking_note_id: number }, useGlobalLoader: boolean) {
  return await APIDelete(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking/note"), params, useGlobalLoader);
}

// Get Booking Audit Trails
export async function GetAuditTrails(params: any, useGlobalLoader: boolean, token?: CancelToken) {
  return await APIGet(
    APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking/audit"),
    params,
    useGlobalLoader,
    token,
  );
}

// Move Booking
export async function MoveBooking(params: any, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking/move"), params, useGlobalLoader);
}

// Copy Booking
export async function CopyBooking(params: any, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking/copy"), params, useGlobalLoader);
}

export async function GetCartWaiver(
  params: { customer_id?: number; tee_time_id?: number; order_id?: number; test?: boolean },
  useGlobalLoader?: boolean,
) {
  return await APIGet(
    APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking/cart-waiver"),
    params,
    useGlobalLoader,
  );
}

export async function PutBookingConfirmation(
  params: IPutBookingConfirmation,
  useGlobalLoader?: boolean,
  cancelToken?: CancelToken,
) {
  return await APIPut(
    APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking/confirmation"),
    params,
    useGlobalLoader,
    cancelToken,
  );
}

export async function PutCopyBooking(
  params: { booking_id: number; tee_time_ids: Array<number> },
  useGlobalLoader: boolean,
) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking/copy"), params, useGlobalLoader);
}

export async function GetValidateCopyMultipleBookings(
  params: { booking_id: number; start_date: string; end_date: string; occurrences: Array<string> },
  useGlobalLoader: boolean,
) {
  return await APIGet<IValidateDuplicateBookings>(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking/copy/validate"), params, useGlobalLoader);
}

interface IGetBookingRestriction {
  status: StatusCode;
  message?: string;
  data: TBookingRestriction[];
}

export async function GetBookingRestrictions(params: { id?: number, extended?: boolean }, useGlobalLoader: boolean, token?: CancelToken): Promise<IGetBookingRestriction> {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking/restrictions"), params, useGlobalLoader, token);
}

interface IPostBookingRestriction {
  start_time: string;
  end_time: string;
  day_of_week: string;
  block_bookings: boolean;
  customer_type_ids: number[];
}

export async function PostBookingRestriction(params: IPostBookingRestriction, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking/restrictions"), params, useGlobalLoader);
}

interface IPutBookingRestriction {
  id: number | string;
  start_time: string;
  end_time: string;
  day_of_week: string;
  block_bookings: boolean;
  customer_type_id: number;
}

export async function PutBookingRestriction(params: IPutBookingRestriction, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking/restrictions"), params, useGlobalLoader);
}

export async function DeleteBookingRestriction(params: { id: number | string }, useGlobalLoader: boolean) {
  return await APIDelete(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking/restrictions"), params, useGlobalLoader);
}

interface IPutDuplicateBookingRestriction {
  id: string | number;
  customer_type_ids: number[];
  days_of_week: string[];
}

export async function PutDuplicateBookingRestriction(params: IPutDuplicateBookingRestriction, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking/restrictions/duplicate"), params, useGlobalLoader);
}

interface IGetCopyLeaguePreview {
  booking_ids: Array<number>;
  target_date: string;
  start_time: string;
  end_time: string;
  league_id: number;
  division_id: number;
}

interface IPutCopyLeagueBookings {
  target_date: string;
  start_time: string;
  end_time: string;
  league_id: number;
  division_id: number;
  booking_ids: Array<number>;
}

export async function GetCopyLeagueBookingPreview(params: IGetCopyLeaguePreview, useGlobalLoader: boolean, token?: CancelToken) {
  return await APIGet(
    APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking/copy-league-preview"),
    params,
    useGlobalLoader,
    token,
  );
}

export async function PutCopyLeagueBookings(params: IPutCopyLeagueBookings, useGlobalLoader: boolean, token?: CancelToken) {
  return await APIPut(
    APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking/copy-league"),
    params,
    useGlobalLoader,
    token,
  );
}